// crewbit-admin/src/components/money/ActivityTimelineCard.tsx
//
// Spec: §InvoiceDetail.D3 timeline. Activity rows with iconForActivityType
// tone-mapping. First 25 rows. Hides on empty (calm-empty per §1.4).

import { palette, spacing, typography, urgency } from '../../styles/tokens';
import { iconForActivityType, type TimelineTone } from '../../lib/activityTimelineIcons';
import type { InvoiceActivityRow } from '../../hooks/useInvoiceActivity';

interface Props {
  activity: InvoiceActivityRow[] | null;
  loading: boolean;
}

const TONE_FG: Record<TimelineTone, string> = {
  neutral: palette.g500,
  info: palette.accent,
  success: palette.green,
  warning: urgency.warning,
  danger: palette.red,
};

const TONE_BG: Record<TimelineTone, string> = {
  neutral: palette.g100,
  info: palette.accentWash,
  success: palette.greenWash,
  warning: palette.amberWash,
  danger: palette.redWash,
};

function fmtRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  const date = new Date(iso);
  return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

export function ActivityTimelineCard({ activity, loading }: Props) {
  if (loading && !activity) {
    return (
      <div
        style={{
          background: palette.white,
          border: `1px solid ${palette.g150}`,
          borderRadius: 12,
          padding: spacing.md,
          marginBottom: spacing.md,
          fontSize: typography.size.caption,
          color: palette.g400,
        }}
      >
        Loading activity…
      </div>
    );
  }
  if (!activity || activity.length === 0) return null;

  return (
    <div
      style={{
        background: palette.white,
        border: `1px solid ${palette.g150}`,
        borderRadius: 12,
        padding: spacing.md,
        marginBottom: spacing.md,
      }}
    >
      <div style={{ fontSize: typography.size.caption, fontWeight: typography.weight.semibold, color: palette.g700, marginBottom: spacing.sm }}>
        Activity
      </div>

      <div>
        {activity.slice(0, 25).map((row) => {
          const { Icon, tone } = iconForActivityType(row.activity_type);
          return (
            <div
              key={row.id}
              style={{
                display: 'grid',
                gridTemplateColumns: '32px 1fr auto',
                gap: spacing.sm,
                alignItems: 'flex-start',
                padding: `${spacing.sm}px 0`,
                borderBottom: `1px solid ${palette.g100}`,
              }}
            >
              <div
                style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: TONE_BG[tone],
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >
                <Icon size={14} color={TONE_FG[tone]} strokeWidth={2} />
              </div>
              <div style={{ fontSize: typography.size.caption, color: palette.g700, lineHeight: 1.4 }}>
                {row.description}
              </div>
              <div style={{ fontSize: typography.size.micro, color: palette.g400, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {fmtRelative(row.created_at)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
